import React, { forwardRef } from "react";

import { DisplayNumberProps } from "./types";
import { formatCurrency, formatNumber } from "../../utils";
import { Tooltip } from "../Tooltip";
import { Typography } from "../Typography/Typography";

export const DisplayCurrency = forwardRef<
  HTMLSpanElement,
  Omit<DisplayNumberProps, "shortenThreshold"> & {
    currencyCode?: string;
    currencyPosition?: string;
    currencyClassName?: string;
  }
>(
  (
    {
      value,
      variant = "Body 2",
      currencyCode = "USD",
      currencyPosition = "right",
      thousandSeparator = ",",
      decimalSeparator = ".",
      trimDecimals = true,
      decimalSpaces = 2,
      format = "long",
      disableTooltip = false,
      className,
      as = "span",
      currencyClassName,
      ...rest
    },
    ref
  ) => {
    const formattedValue = formatNumber(
      value,
      thousandSeparator,
      decimalSeparator,
      trimDecimals,
      decimalSpaces,
      "short",
      10000
    );

    const formattedLongValue = formatNumber(
      value,
      thousandSeparator,
      decimalSeparator,
      trimDecimals,
      decimalSpaces,
      "long",
      10000
    );

    if (format === "short") {
      return (
        <Tooltip
          title={formatCurrency(
            value,
            thousandSeparator,
            decimalSeparator,
            false,
            decimalSpaces,
            "long",
            currencyCode,
            currencyPosition
          )}
          disable={
            disableTooltip ||
            (!formattedLongValue.includes(thousandSeparator) &&
              !formattedLongValue.includes(decimalSeparator))
          }
        >
          <Typography
            as={as}
            variant={variant}
            className={className}
            ref={ref}
            {...rest}
          >
            {currencyPosition === "left" ? (
              <>
                <span className={currencyClassName}>{currencyCode}</span>{" "}
                {formattedValue}
              </>
            ) : (
              <>
                {formattedValue}{" "}
                <span className={currencyClassName}>{currencyCode}</span>
              </>
            )}
          </Typography>
        </Tooltip>
      );
    }

    return (
      <Typography
        as={as}
        variant={variant}
        className={className}
        ref={ref}
        {...rest}
      >
        {currencyPosition === "left" ? (
          <>
            <span className={currencyClassName}>{currencyCode}</span>{" "}
            {formattedLongValue}
          </>
        ) : (
          <>
            {formattedLongValue}{" "}
            <span className={currencyClassName}>{currencyCode}</span>
          </>
        )}
      </Typography>
    );
  }
);

DisplayCurrency.displayName = "DisplayCurrency";
